import { supabase } from '../config/supabase'

/**
 * Obtiene el wedding_id del usuario (considerando si es partner)
 * Mejorado para manejar casos donde el usuario no está conectado a una boda
 */
const getUserWeddingId = async (userId) => {
  try {
    // Primero, obtener el perfil del usuario para verificar si es partner
    const { data: profile, error: profileError } = await supabase
      .from('profiles')
      .select('partner_user_id')
      .eq('id', userId)
      .maybeSingle()

    if (profileError) {
      if (profileError.code === 'PGRST116') {
        // Perfil no existe - esto puede pasar si el usuario acaba de registrarse
        console.warn(`Profile not found for user ${userId}. User may need to complete registration.`)
        return null
      }
      console.error('Error fetching profile:', profileError)
      return null
    }

    if (!profile) {
      console.warn(`No profile found for user ${userId}`)
      return null
    }

    // Determinar el user_id de la boda:
    // - Si el usuario tiene un partner_user_id (es partner), buscar la boda del usuario principal (partner_user_id)
    // - Si el usuario NO tiene partner_user_id pero hay otro perfil que tiene este usuario como partner_user_id,
    //   buscar la boda del usuario mismo (es el usuario principal)
    // - Si no tiene partner, buscar la boda del usuario mismo
    let weddingOwnerId = userId
    
    if (profile.partner_user_id) {
      // Este usuario es un partner, buscar la boda del usuario principal
      weddingOwnerId = profile.partner_user_id
      console.log(`User ${userId} is a partner, looking for wedding owned by ${weddingOwnerId}`)
    } else {
      // Verificar si hay otro perfil que tenga este usuario como partner
      // Si es así, este usuario es el principal y debe buscar su propia boda
      const { data: partnerProfile } = await supabase
        .from('profiles')
        .select('id')
        .eq('partner_user_id', userId)
        .maybeSingle()
      
      if (partnerProfile) {
        console.log(`User ${userId} is the principal, partner profile found: ${partnerProfile.id}`)
      }
      
      // Buscar su propia boda (es el principal)
      weddingOwnerId = userId
    }

    // Buscar la boda del dueño
    const { data: wedding, error: weddingError } = await supabase
      .from('weddings')
      .select('id')
      .eq('user_id', weddingOwnerId)
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle()

    if (weddingError) {
      if (weddingError.code === 'PGRST116') {
        // No hay boda - esto puede pasar si el trigger SQL no se ejecutó o el usuario no completó el registro
        console.warn(`No wedding found for user ${weddingOwnerId}. User may need to complete registration or the wedding may not have been created yet.`)
        return null
      }
      console.error('Error fetching wedding ID:', weddingError)
      return null
    }

    if (!wedding) {
      console.warn(`No wedding found for user ${weddingOwnerId}. The wedding may not have been created yet.`)
      return null
    }

    console.log(`Wedding found for user ${userId}: ${wedding.id} (owned by ${weddingOwnerId})`)
    return wedding.id
  } catch (error) {
    console.error('Error fetching wedding ID:', error)
    return null
  }
}

/**
 * Limpia tareas duplicadas manteniendo solo la más antigua de cada grupo
 */
export const cleanDuplicateTasks = async (userId) => {
  try {
    const weddingId = await getUserWeddingId(userId)
    if (!weddingId) {
      return { cleaned: false, message: 'No wedding found' }
    }

    // Obtener todas las tareas
    const { data: allTasks, error: fetchError } = await supabase
      .from('tasks')
      .select('id, title, description, due_date, created_at')
      .eq('wedding_id', weddingId)
      .order('created_at', { ascending: true })

    if (fetchError) throw fetchError

    if (!allTasks || allTasks.length === 0) {
      return { cleaned: false, message: 'No tasks found' }
    }

    // Agrupar por título y descripción (tareas duplicadas)
    const taskGroups = {}
    allTasks.forEach(task => {
      const key = `${task.title || ''}_${task.description || ''}_${task.due_date || 'no_date'}`
      if (!taskGroups[key]) {
        taskGroups[key] = []
      }
      taskGroups[key].push(task)
    })

    // Encontrar duplicados (grupos con más de 1 tarea)
    const duplicatesToDelete = []
    Object.values(taskGroups).forEach(group => {
      if (group.length > 1) {
        // Mantener la más antigua (primera), eliminar las demás
        const sorted = group.sort((a, b) => 
          new Date(a.created_at) - new Date(b.created_at)
        )
        // Agregar todas excepto la primera a la lista de eliminación
        duplicatesToDelete.push(...sorted.slice(1).map(t => t.id))
      }
    })

    if (duplicatesToDelete.length === 0) {
      return { cleaned: false, message: 'No duplicates found' }
    }

    // Eliminar duplicados
    const { error: deleteError } = await supabase
      .from('tasks')
      .delete()
      .in('id', duplicatesToDelete)

    if (deleteError) throw deleteError

    return { 
      cleaned: true, 
      removed: duplicatesToDelete.length,
      message: `Removed ${duplicatesToDelete.length} duplicate tasks`
    }
  } catch (error) {
    console.error('Error cleaning duplicate tasks:', error)
    return { cleaned: false, error: error.message }
  }
}

/**
 * Obtiene todas las tareas del usuario ordenadas por fecha
 * Optimizado para mejor rendimiento
 */
export const getTasks = async (userId) => {
  try {
    const weddingId = await getUserWeddingId(userId)
    if (!weddingId) {
      return []
    }

    // Optimizado: seleccionar solo las columnas necesarias
    const { data, error } = await supabase
      .from('tasks')
      .select('id, title, description, due_date, is_completed, tip_from_collective, user_notes, created_at, updated_at')
      .eq('wedding_id', weddingId)
      .order('due_date', { ascending: true, nullsLast: true })

    if (error) throw error

    return data || []
  } catch (error) {
    console.error('Error fetching tasks:', error)
    return []
  }
}

/**
 * Crea una nueva tarea
 */
export const createTask = async (userId, taskData) => {
  try {
    const weddingId = await getUserWeddingId(userId)
    if (!weddingId) {
      throw new Error('No wedding found for user')
    }

    if (process.env.NODE_ENV === 'development') {
      console.log('createTask - weddingId:', weddingId, 'userId:', userId)
    }

    const taskToInsert = {
      wedding_id: weddingId,
      title: taskData.title || taskData.description || 'Nueva tarea', // title es NOT NULL
      description: taskData.description || null,
      due_date: taskData.due_date || null,
      tip_from_collective: taskData.tip_from_collective || null,
      user_notes: taskData.user_notes || null,
      is_completed: false
    }

    if (process.env.NODE_ENV === 'development') {
      console.log('createTask - inserting:', taskToInsert)
    }

    const { data, error } = await supabase
      .from('tasks')
      .insert(taskToInsert)
      .select()
      .single()

    if (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('createTask - Supabase error:', error)
      }
      throw error
    }

    return data
  } catch (error) {
    console.error('Error creating task:', error)
    throw error
  }
}

/**
 * Actualiza una tarea
 */
export const updateTask = async (taskId, updates) => {
  try {
    const { data, error } = await supabase
      .from('tasks')
      .update(updates)
      .eq('id', taskId)
      .select()
      .single()

    if (error) throw error

    return data
  } catch (error) {
    console.error('Error updating task:', error)
    throw error
  }
}

/**
 * Elimina una tarea
 */
export const deleteTask = async (taskId) => {
  try {
    const { error } = await supabase
      .from('tasks')
      .delete()
      .eq('id', taskId)

    if (error) throw error

    return true
  } catch (error) {
    console.error('Error deleting task:', error)
    throw error
  }
}

/**
 * Calcula la fecha basada en el tiempo relativo a la fecha de boda
 */
export const calculateDueDate = async (userId, timeBefore) => {
  try {
    const weddingId = await getUserWeddingId(userId)
    if (!weddingId) {
      return null
    }

    const { data, error } = await supabase
      .from('weddings')
      .select('event_date')
      .eq('id', weddingId)
      .single()

    if (error || !data?.event_date) {
      return null
    }

    const weddingDate = new Date(data.event_date)
    const dueDate = new Date(weddingDate)

    // Calcular la fecha basada en el tiempo antes de la boda
    switch (timeBefore) {
      case '12 months before':
      case '12 meses antes': // Mantener compatibilidad con datos existentes
        dueDate.setMonth(dueDate.getMonth() - 12)
        break
      case '10 months before':
      case '10 meses antes':
        dueDate.setMonth(dueDate.getMonth() - 10)
        break
      case '9 months before':
      case '9 meses antes':
        dueDate.setMonth(dueDate.getMonth() - 9)
        break
      case '8 months before':
      case '8 meses antes':
        dueDate.setMonth(dueDate.getMonth() - 8)
        break
      case '6 months before':
      case '6 meses antes':
        dueDate.setMonth(dueDate.getMonth() - 6)
        break
      case '5 months before':
      case '5 meses antes':
        dueDate.setMonth(dueDate.getMonth() - 5)
        break
      case '3 months before':
      case '3 meses antes':
        dueDate.setMonth(dueDate.getMonth() - 3)
        break
      case '2 months before':
      case '2 meses antes':
        dueDate.setMonth(dueDate.getMonth() - 2)
        break
      case '1 month before':
      case '1 mes antes':
        dueDate.setMonth(dueDate.getMonth() - 1)
        break
      case '3 weeks before':
      case '3 semanas antes':
        dueDate.setDate(dueDate.getDate() - 21)
        break
      case '2 weeks before':
      case '2 semanas antes':
        dueDate.setDate(dueDate.getDate() - 14)
        break
      case '1 week before':
      case '1 semana antes':
        dueDate.setDate(dueDate.getDate() - 7)
        break
      case '3 days before':
      case '3 días antes':
        dueDate.setDate(dueDate.getDate() - 3)
        break
      case '1 day before':
      case '1 día antes':
        dueDate.setDate(dueDate.getDate() - 1)
        break
      default:
        return null
    }

    return dueDate.toISOString().split('T')[0] // Retorna en formato YYYY-MM-DD
  } catch (error) {
    console.error('Error calculating due date:', error)
    return null
  }
}

/**
 * Inicializa las tareas predefinidas para un usuario
 * Incluye verificaciones dobles para prevenir condiciones de carrera
 */
export const initializeDefaultTasks = async (userId, defaultTasks) => {
  try {
    const weddingId = await getUserWeddingId(userId)
    if (!weddingId) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('initializeDefaultTasks - No wedding found for user:', userId)
      }
      throw new Error('No wedding found for user')
    }

    if (process.env.NODE_ENV === 'development') {
      console.log('initializeDefaultTasks - weddingId:', weddingId, 'userId:', userId)
    }

    // VERIFICACIÓN CRÍTICA: Verificar si ya hay tareas directamente en la base de datos
    // Esta es la verificación más importante - si hay CUALQUIER tarea, NO inicializar
    const { count: existingTasksCount, error: countError } = await supabase
      .from('tasks')
      .select('*', { count: 'exact', head: true })
      .eq('wedding_id', weddingId)
    
    if (countError) {
      console.error('Error checking existing tasks:', countError)
      // Si hay error al verificar, NO inicializar para evitar duplicaciones
      throw new Error('Cannot verify existing tasks. Initialization aborted to prevent duplicates.')
    }
    
    // Si hay CUALQUIER tarea (incluso 1), NO inicializar
    if (existingTasksCount && existingTasksCount > 0) {
      if (process.env.NODE_ENV === 'development') {
        console.log(`initializeDefaultTasks - ${existingTasksCount} tasks already exist for wedding ${weddingId}, skipping initialization`)
      }
      return { initialized: false, message: `Tasks already exist (${existingTasksCount} tasks found)` }
    }

    // Verificación adicional justo antes de insertar (sin delay para mejor rendimiento)
    const { count: existingTasksCount2, error: countError2 } = await supabase
      .from('tasks')
      .select('*', { count: 'exact', head: true })
      .eq('wedding_id', weddingId)
    
    if (countError2) {
      console.error('Error checking existing tasks (pre-insert check):', countError2)
      throw new Error('Cannot verify existing tasks before insert. Initialization aborted.')
    }
    
    if (existingTasksCount2 && existingTasksCount2 > 0) {
      if (process.env.NODE_ENV === 'development') {
        console.log(`initializeDefaultTasks - ${existingTasksCount2} tasks found in pre-insert check, another initialization completed. Skipping.`)
      }
      return { initialized: false, message: `Tasks already exist (${existingTasksCount2} tasks found - detected before insert)` }
    }

    // Crear todas las tareas
    const tasksToCreate = await Promise.all(
      defaultTasks.map(async (task) => {
        let dueDate = null
        if (task.time_before) {
          dueDate = await calculateDueDate(userId, task.time_before)
        }

        return {
          wedding_id: weddingId,
          title: task.title || task.description || 'Tarea', // title es NOT NULL
          description: task.description || null,
          due_date: dueDate,
          tip_from_collective: task.tip_from_collective || null,
          user_notes: null,
          is_completed: false
        }
      })
    )

    if (process.env.NODE_ENV === 'development') {
      console.log('initializeDefaultTasks - Creating', tasksToCreate.length, 'tasks')
    }

    const { data, error } = await supabase
      .from('tasks')
      .insert(tasksToCreate)
      .select()

    if (error) {
      // Si el error es por duplicación (aunque no debería pasar con las verificaciones),
      // verificar si las tareas ya existen y retornar éxito
      if (error.code === '23505' || error.message?.includes('duplicate') || error.message?.includes('unique')) {
        console.warn('initializeDefaultTasks - Duplicate key error detected, verifying if tasks were created')
        const { count: finalCount } = await supabase
          .from('tasks')
          .select('*', { count: 'exact', head: true })
          .eq('wedding_id', weddingId)
        
        if (finalCount && finalCount > 0) {
          console.log(`initializeDefaultTasks - Tasks already exist (${finalCount} tasks), initialization completed by another process`)
          return { initialized: false, message: `Tasks already exist (${finalCount} tasks found)` }
        }
      }
      
      if (process.env.NODE_ENV === 'development') {
        console.error('initializeDefaultTasks - Supabase error:', error)
      }
      throw error
    }

    // VERIFICACIÓN FINAL: Verificar que las tareas se insertaron correctamente
    // Esto es una verificación adicional de seguridad después del insert
    const { count: insertedCount } = await supabase
      .from('tasks')
      .select('*', { count: 'exact', head: true })
      .eq('wedding_id', weddingId)
    
    if (process.env.NODE_ENV === 'development') {
      console.log(`initializeDefaultTasks - Successfully created ${data.length} tasks. Total tasks in DB: ${insertedCount}`)
    }

    return { initialized: true, count: data.length }
  } catch (error) {
    console.error('Error initializing default tasks:', error)
    throw error
  }
}
